const { v4: uuidv4 } = require('uuid');
const {
  setCookie,
  deleteCookie,
  getCookie,
  setItemInSessionStorage,
  removeItemInSessionStorage,
  removeItemInLocalStorage,
  getItemInSessionStorage,
  setItemInSessionStorageWithoutStringify,
  deleteCookieWithoutDomain,
} = require('./lib/browserData');
const catalystLogin = require('./apis/catalystLogin');
const catalystLogout = require('./apis/catalystLogout');
const catalystSoftLogin = require('./apis/catalystSoftLogin');
const catalystMergeCart = require('./apis/catalystMergeCart');
const { getConsentDateInLocalTimeFormatWithTimeZone } = require('./utils/dateTimeUtils');
const { COOKIES, LOGIN_STATUS, SESSION_STORAGE, LOCAL_STORAGE } = require('./constants');
const catalystGetCartCount = require('./apis/catalystGetCartCount');
const catalystGetCart = require('./apis/catalystGetCart');
const catalystAssociateAccount = require('./apis/catalystAssociateAccount');
const catalystAuthValidate = require('./apis/catalystAuthValidate');
const catalystUserStatus = require('./apis/catalystUserStatus');
const catalystResetPasswordWithCurrent = require('./apis/catalystResetPasswordWithCurrent');
const catalystResetPasswordWithoutCurrent = require('./apis/catalystResetPasswordWithoutCurrent');
const catalystLegalTextConsent = require('./apis/catalystLegalTextConsent');
const catalystConsentTemplate = require('./apis/catalystConsentTemplate');
const catalystConsentTemplateV2 = require('./apis/catalystConsentTemplateV2');
const catalystSaveUserConsent = require('./apis/catalystUserConsent');
const catalystGetOtpByEmail = require('./apis/catalystOtpByEmail');
const catalystGetOtpBySMS = require('./apis/catalystOtpBySMS');
const catalystGetOtpByChallenge = require('./apis/catalystOtpByChallenge');
const catalystGetOtpChallengeValidated = require('./apis/catalystOtpChallengeValidation');
const catalystUpdateUser = require('./apis/catalystUpdateUser');
const catalystGetUser = require('./apis/catalystGetUser');
const { decodeJWT } = require('./utils/decoder');
const catalystGetAddresses = require('./apis/catalystGetAddress');

const INVALID_OPERATION_COMBINATION_ERROR = {
  code: 'CATALYST_LOGIN_ERROR_INVALID_OPERATION_COMBINATION',
  message: 'cart merge and cart sync are not allowed with associate cart',
};

const LOGIN_WITH_TOKEN_CHECKOUT_INVALID_SESSION = {
  code: 'LOGIN_WITH_TOKEN_CHECKOUT_INVALID_SESSION',
  message: 'Information required to login with token is insufficient',
};

const CATALYST_LOGIN_ERROR_MISSING_DATA = {
  code: 'CATALYST_LOGIN_ERROR_MISSING_DATA',
  message: 'Missing information to set cookie',
};

async function associateAccount(tenantId, catalystBaseUrl, additionalInfo = {}) {
  const { cartId, errors } = await catalystAssociateAccount(
    tenantId,
    additionalInfo,
    catalystBaseUrl,
  );
  if (!cartId) return errors;
  const { whitelabel = {} } = additionalInfo;
  if (whitelabel.enabled && !whitelabel.multiSite) {
    return errors;
  }
  setCookie(
    COOKIES.CART_ID.VALUE,
    cartId,
    tenantId,
    COOKIES.CART_ID.EXPIRY,
    true,
    whitelabel.domainName,
  );
  return errors;
}

async function mergeCart(tenantId, catalystBaseUrl, whitelabel) {
  const { cartId, errors } = await catalystMergeCart(tenantId, whitelabel, catalystBaseUrl);
  if (!cartId) return errors;
  setCookie(
    COOKIES.CART_ID.VALUE,
    cartId,
    tenantId,
    COOKIES.CART_ID.EXPIRY,
    true,
    whitelabel.domainName,
  );
  return errors;
}

async function getCartCount(tenantId, catalystBaseUrl, additionalInfo = {}) {
  const { whitelabel = {} } = additionalInfo;

  const cartId =
    whitelabel.enabled && !whitelabel.multiSite
      ? whitelabel.cartId
      : getCookie(COOKIES.CART_ID.VALUE);

  if (cartId) {
    return catalystGetCartCount(cartId, tenantId, catalystBaseUrl, additionalInfo);
  }
  const { data: { cartId: getCartCartId, totalProductsQuantityCount } = {}, errors } =
    await catalystGetCart(tenantId, catalystBaseUrl, additionalInfo);
  if (!getCartCartId) return { cartCount: undefined, errors };
  setCookie(
    COOKIES.CART_ID.VALUE,
    getCartCartId,
    tenantId,
    COOKIES.CART_ID.EXPIRY,
    true,
    whitelabel.domainName,
  );
  return { cartCount: totalProductsQuantityCount, errors };
}

function setUserCookies({
  firstName,
  lastName,
  cartCount,
  userId,
  tenantId,
  domainName,
  segments,
}) {
  setCookie(
    COOKIES.DYNAMIC_DATA.VALUE,
    `${encodeURIComponent(firstName)}*${cartCount || 0}*${encodeURIComponent(lastName)}`,
    tenantId,
    COOKIES.DYNAMIC_DATA.EXPIRY,
    true,
    domainName,
  );
  setCookie(
    COOKIES.CATALYST_DATA.VALUE,
    `${encodeURIComponent(firstName)}*${cartCount || 0}*${encodeURIComponent(lastName)}`,
    tenantId,
    COOKIES.CATALYST_DATA.EXPIRY,
    true,
    domainName,
  );
  setCookie(
    COOKIES.USER_ID.VALUE,
    userId || '',
    tenantId,
    COOKIES.USER_ID.EXPIRY,
    true,
    domainName,
  );
  if (segments) {
    setCookie(
      COOKIES.USER_SEGMENT.VALUE,
      Buffer.from(JSON.stringify(segments)).toString('base64'),
      tenantId,
      COOKIES.USER_SEGMENT.EXPIRY,
      true,
      domainName,
    );
  }
}

function removeUserCookies(tenantId, domainName) {
  deleteCookie(COOKIES.SESSION.VALUE, tenantId, domainName);
  deleteCookie(COOKIES.CART_ID.VALUE, tenantId, domainName);
  deleteCookie(COOKIES.USER_ID.VALUE, tenantId, domainName);
  deleteCookie(COOKIES.CMR_POINTS, tenantId, domainName);
  deleteCookie(COOKIES.USER_FPAY_LINK_STATUS.VALUE, tenantId, domainName);
  deleteCookie(COOKIES.ADDRESS_LINES, tenantId, domainName);
  deleteCookie(COOKIES.ADDRESS_ID, tenantId, domainName);
  deleteCookie(COOKIES.POLITICAL_ID, tenantId, domainName);
  deleteCookie(COOKIES.CARTLINE_QUANTITIES, tenantId, domainName);
  deleteCookieWithoutDomain(COOKIES.F_AUTH_DEVICE_FIRST_SEEN_DATE, { sameSite: 'Strict' });
  deleteCookieWithoutDomain(COOKIES.F_AUTH_DEVICE_ID, { sameSite: 'Strict' });
  deleteCookieWithoutDomain(COOKIES.F_AUTH_DEVICE_RECOGNIZED, { sameSite: 'Strict' });
  deleteCookie(COOKIES.USER_SEGMENT, tenantId, domainName);
  setCookie(
    COOKIES.DYNAMIC_DATA.VALUE,
    '*0',
    tenantId,
    COOKIES.DYNAMIC_DATA.EXPIRY,
    true,
    domainName,
  );
  setCookie(
    COOKIES.CATALYST_DATA.VALUE,
    '*0',
    tenantId,
    COOKIES.CATALYST_DATA.EXPIRY,
    true,
    domainName,
  );
  removeItemInSessionStorage(SESSION_STORAGE.CATALYST_EMAIL_ID);
  removeItemInSessionStorage(SESSION_STORAGE.CATALYST_USER_TYPE);
  removeItemInLocalStorage(LOCAL_STORAGE.USER_FPAY_LINK_TOKEN);
  removeItemInSessionStorage(SESSION_STORAGE.DIGITAL_DATA);
  removeItemInLocalStorage(LOCAL_STORAGE.CATALYST_EMAIL_ID);
  removeItemInLocalStorage(LOCAL_STORAGE.CATALYST_USER_TYPE);
  removeItemInLocalStorage(LOCAL_STORAGE.USER_REQUIRED_FILEDS);
  removeItemInLocalStorage(LOCAL_STORAGE.ADDRESS_LINES);
  removeItemInLocalStorage(LOCAL_STORAGE.COMUNA_DATA);
  removeItemInLocalStorage(LOCAL_STORAGE.REGION_DATA);
}

async function login(
  email,
  password,
  tenantId,
  doMergeCart,
  doAssociateAccount,
  catalystBaseUrl,
  isOTPLogin = false,
  otpType = null,
  additionalHeaders = {},
  additionalInfo = {},
  isEnableNewOtpFlow = false,
  isEmailPrefixEligibleForOtpModal = false,
) {
  if (doAssociateAccount && [doMergeCart].includes(true)) {
    return {
      loginStatus: false,
      data: undefined,
      errors: [INVALID_OPERATION_COMBINATION_ERROR],
    };
  }
  deleteCookieWithoutDomain(COOKIES.F_AUTH_DEVICE_FIRST_SEEN_DATE, { sameSite: 'Strict' });
  deleteCookieWithoutDomain(COOKIES.F_AUTH_DEVICE_ID, { sameSite: 'Strict' });
  deleteCookieWithoutDomain(COOKIES.F_AUTH_DEVICE_RECOGNIZED, { sameSite: 'Strict' });
  const {
    data,
    data: { sessionId } = {},
    errors,
    response,
  } = await catalystLogin(
    email,
    password,
    tenantId,
    catalystBaseUrl,
    isOTPLogin,
    otpType,
    additionalHeaders,
    additionalInfo,
  );

  const { whitelabel = {} } = additionalInfo;
  let mergeCartErrors = [];
  let cartCountErrors = [];

  if (!data) return { loginStatus: false, data: undefined, errors };

  const isDeviceRecognised = response.headers.get('X-F-Auth-Device-Recognized') !== 'false';
  const fAuthDeviceId = response.headers.get('X-F-Auth-Device-Id');
  setItemInSessionStorage(SESSION_STORAGE.IS_DEVICE_RECOGNISED, isDeviceRecognised);
  setItemInSessionStorageWithoutStringify(SESSION_STORAGE.F_AUTH_DEVICE_ID, fAuthDeviceId);

  if (typeof window !== 'undefined') {
    window.digitalData = {
      ...(window.digitalData || {}),
      Devicerecognized: isDeviceRecognised,
    };
  }

  if (isDeviceRecognised || !isEnableNewOtpFlow || !isEmailPrefixEligibleForOtpModal) {
    const {
      session: { status: sessionStatus },
      user: {
        userName: { firstName, lastName1: lastName } = {},
        userId,
        email: { emailId },
        segments,
      },
    } = data;
    setItemInSessionStorage(SESSION_STORAGE.CATALYST_EMAIL_ID, emailId);
    setItemInSessionStorage(SESSION_STORAGE.CATALYST_USER_TYPE, sessionStatus);
    setCookie(
      COOKIES.SESSION.VALUE,
      sessionId,
      tenantId,
      COOKIES.SESSION.EXPIRY,
      true,
      whitelabel.domainName,
    );

    const associateAccountErrors = doAssociateAccount
      ? await associateAccount(tenantId, catalystBaseUrl, additionalInfo)
      : [];

    const defaultAddress = doAssociateAccount
      ? undefined
      : await catalystGetAddresses(tenantId, catalystBaseUrl, additionalInfo);

    if (defaultAddress && defaultAddress.id) {
      setCookie(
        COOKIES.ADDRESS_ID,
        defaultAddress.id,
        tenantId,
        COOKIES.SESSION.EXPIRY,
        true,
        whitelabel.domainName,
      );
      setCookie(
        COOKIES.ADDRESS_LINES,
        encodeURIComponent(
          `${defaultAddress.addressLine1}*${defaultAddress.addressLine2}**${defaultAddress.latitude}*${defaultAddress.longitude}`,
        ),
        tenantId,
        COOKIES.SESSION.EXPIRY,
        true,
        whitelabel.domainName,
        false,
      );
    }

    mergeCartErrors = doMergeCart ? await mergeCart(tenantId, catalystBaseUrl, whitelabel) : [];

    const { cartCount, errors: cartCountErrorLog } = await getCartCount(
      tenantId,
      catalystBaseUrl,
      additionalInfo,
    );

    cartCountErrors = cartCountErrorLog;

    if ([firstName, lastName].includes(undefined)) {
      return {
        loginStatus: isDeviceRecognised,
        data,
        errors: [
          ...associateAccountErrors,
          ...mergeCartErrors,
          ...cartCountErrors,
          CATALYST_LOGIN_ERROR_MISSING_DATA,
        ],
        ...(isDeviceRecognised ? { isDeviceRecognised } : {}),
      };
    }
    const emailAsFirstName = emailId.split('@')[0];
    setUserCookies({
      firstName: firstName || emailAsFirstName,
      lastName,
      cartCount,
      userId,
      tenantId,
      domainName: whitelabel.domainName,
      segments,
    });
  }

  return {
    loginStatus: isEnableNewOtpFlow && isEmailPrefixEligibleForOtpModal ? isDeviceRecognised : true,
    data,
    errors: [...mergeCartErrors, ...cartCountErrors],
    isDeviceRecognised,
  };
}

function decodeIDToken(idToken) {
  if (!idToken || typeof idToken !== 'string') return {};
  try {
    const {
      user,
      session: { status: sessionStatus },
    } = decodeJWT(idToken);
    return { user, sessionStatus };
  } catch {
    return {};
  }
}

async function loginWithToken(loginDataResponse, tenantId, additionalInfo) {
  const { sessionId, user: userFromResponse, userType, idToken } = loginDataResponse;
  const { user: userFromIDToken, sessionStatus: sessionStatusFromIDToken } = decodeIDToken(idToken);
  const { whitelabel = {} } = additionalInfo;

  const user = userFromIDToken || userFromResponse;
  const sessionStatus = sessionStatusFromIDToken || userType;

  if (!user || !sessionId) {
    return {
      loginStatus: false,
      data: undefined,
      errors: [LOGIN_WITH_TOKEN_CHECKOUT_INVALID_SESSION],
    };
  }

  const {
    userName: { firstName, lastName1: lastName } = {},
    userId,
    email: { emailId },
    segments,
  } = user;

  setItemInSessionStorage(SESSION_STORAGE.CATALYST_EMAIL_ID, emailId);
  setItemInSessionStorage(SESSION_STORAGE.CATALYST_USER_TYPE, sessionStatus);
  setCookie(
    COOKIES.SESSION.VALUE,
    sessionId,
    tenantId,
    COOKIES.SESSION.EXPIRY,
    true,
    whitelabel.domainName,
  );

  const data = { ...loginDataResponse, user, userType: sessionStatus };

  if (!firstName || !lastName) {
    return { loginStatus: true, data, errors: [CATALYST_LOGIN_ERROR_MISSING_DATA] };
  }

  setUserCookies({
    firstName,
    lastName,
    userId,
    tenantId,
    domainName: whitelabel.domainName,
    segments,
  });
  return { loginStatus: true, data, errors: [] };
}

async function softLogin(email, tenantId, catalystBaseUrl, additionalInfo) {
  const {
    data,
    data: { sessionId } = {},
    errors,
  } = await catalystSoftLogin(email, tenantId, additionalInfo, catalystBaseUrl);

  const { whitelabel = {} } = additionalInfo;

  if (!data) {
    setItemInSessionStorage(SESSION_STORAGE.CATALYST_EMAIL_ID, email);
    setItemInSessionStorage(SESSION_STORAGE.CATALYST_USER_TYPE, LOGIN_STATUS.GUEST);
    return { loginStatus: false, data: undefined, errors };
  }

  const {
    session: { status: sessionStatus },
    user: {
      userName: { firstName, lastName1: lastName } = {},
      userId,
      email: { emailId },
      segments,
    },
  } = data;
  removeItemInLocalStorage(LOCAL_STORAGE.CATALYST_EMAIL_ID);
  removeItemInLocalStorage(LOCAL_STORAGE.CATALYST_USER_TYPE);
  removeItemInLocalStorage(LOCAL_STORAGE.USER_REQUIRED_FILEDS);
  setItemInSessionStorage(SESSION_STORAGE.CATALYST_EMAIL_ID, emailId);
  setItemInSessionStorage(SESSION_STORAGE.CATALYST_USER_TYPE, sessionStatus);
  setCookie(
    COOKIES.SESSION.VALUE,
    sessionId,
    tenantId,
    COOKIES.SESSION.EXPIRY,
    true,
    whitelabel.domainName,
  );

  const { cartCount } = await getCartCount(tenantId, catalystBaseUrl, additionalInfo);

  setUserCookies({
    firstName,
    lastName,
    cartCount,
    userId,
    tenantId,
    domainName: whitelabel.domainName,
    segments,
  });

  return {
    loginStatus: true,
    data,
    errors,
  };
}

async function logout(tenantId, catalystBaseUrl, additionalInfo, otpFlow) {
  const response = await catalystLogout(tenantId, catalystBaseUrl, additionalInfo, otpFlow);
  if (!response.success) return response;
  const domainName =
    additionalInfo && additionalInfo.whitelabel ? additionalInfo.whitelabel.domainName : undefined;
  removeUserCookies(tenantId, domainName);
  return response;
}

async function isLoggedIn(baseUrl, cookies, channelId, tenantId, additionalInfo) {
  const sessionId = getCookie(COOKIES.SESSION.VALUE, cookies);
  if (!sessionId) {
    return false;
  }
  const { active, status, remainingSessionTime } = await catalystAuthValidate(
    baseUrl,
    sessionId,
    tenantId,
    channelId,
    additionalInfo,
  );
  const isUserActive = active && status === LOGIN_STATUS.AUTHENTICATED;
  if (remainingSessionTime && isUserActive)
    setCookie(COOKIES.SESSION.VALUE, sessionId, tenantId, remainingSessionTime, true);

  return isUserActive;
}

async function userStatus(tenantId, catalystBaseUrl, params) {
  return catalystUserStatus(tenantId, catalystBaseUrl, params);
}

async function resetPasswordWithCurrent(currentPassword, newPassword, tenantId, catalystBaseUrl) {
  return catalystResetPasswordWithCurrent(currentPassword, newPassword, tenantId, catalystBaseUrl);
}

async function resetPasswordWithoutCurrent(
  newPassword,
  confirmNewPassword,
  tenantId,
  catalystBaseUrl,
) {
  return catalystResetPasswordWithoutCurrent(
    newPassword,
    confirmNewPassword,
    tenantId,
    catalystBaseUrl,
  );
}

async function legalTextConsent(consentId, tenantId, catalystBaseUrl) {
  return catalystLegalTextConsent(consentId, tenantId, catalystBaseUrl);
}

async function consentTemplate(purpose, tenantId, catalystBaseUrl) {
  return catalystConsentTemplate(purpose, tenantId, catalystBaseUrl);
}

async function consentTemplateV2(userEmail, purpose, tenantId, catalystBaseUrl) {
  return catalystConsentTemplateV2(userEmail, purpose, tenantId, catalystBaseUrl);
}

async function saveUserConsent(consent, tenantId, catalystBaseUrl) {
  const now = () => new Date();
  // TODO: Remove once all consumers switch to getConsentBody function
  const newConsent = {
    context: {
      dateTimeChannel: now(),
      experience: 'LOG',
    },
    evidence: {
      deviceIdentifier: uuidv4(),
    },
    consentDate: getConsentDateInLocalTimeFormatWithTimeZone(),
    ...consent,
  };
  return catalystSaveUserConsent(newConsent, tenantId, catalystBaseUrl);
}

async function getOTPChallengeValidationURL(
  tenantId,
  challengeId,
  otp,
  catalystBaseUrl,
  doAssociateAccount,
  doMergeCart,
  isEmailFlow,
  otpFlow,
  additionalInfo = {},
) {
  const response = await catalystGetOtpChallengeValidated(
    tenantId,
    challengeId,
    otp,
    catalystBaseUrl,
    isEmailFlow,
    otpFlow,
  );

  const { data = {}, errors = [] } = response;
  if (data.success === true) {
    const userData = JSON.parse(getItemInSessionStorage(SESSION_STORAGE.USER_DATA));
    const { whitelabel = {} } = additionalInfo;
    removeItemInSessionStorage(SESSION_STORAGE.USER_DATA);
    const { idToken: sessionJWT, sessionId } = userData;
    const sessionData = decodeJWT(sessionJWT);
    const datas = { ...userData, ...sessionData };

    const {
      session: { status: sessionStatus },
      user: {
        userName: { firstName, lastName1: lastName } = {},
        userId,
        email: { emailId },
        segments,
      },
    } = datas;
    setItemInSessionStorage(SESSION_STORAGE.CATALYST_EMAIL_ID, emailId);
    setItemInSessionStorage(SESSION_STORAGE.CATALYST_USER_TYPE, sessionStatus);
    setCookie(
      COOKIES.SESSION.VALUE,
      sessionId,
      tenantId,
      COOKIES.SESSION.EXPIRY,
      true,
      whitelabel.domainName,
    );

    const associateAccountErrors = doAssociateAccount
      ? await associateAccount(tenantId, catalystBaseUrl, additionalInfo)
      : [];

    const defaultAddress = doAssociateAccount
      ? undefined
      : await catalystGetAddresses(tenantId, catalystBaseUrl, additionalInfo);

    if (defaultAddress && defaultAddress.id) {
      setCookie(
        COOKIES.ADDRESS_ID,
        defaultAddress.id,
        tenantId,
        COOKIES.SESSION.EXPIRY,
        true,
        whitelabel.domainName,
      );
      setCookie(
        COOKIES.ADDRESS_LINES,
        `${defaultAddress.addressLine1}*${defaultAddress.addressLine2}**${defaultAddress.latitude}*${defaultAddress.longitude}`,
        tenantId,
        COOKIES.SESSION.EXPIRY,
        true,
        whitelabel.domainName,
        false,
      );
      setCookie(
        COOKIES.POLITICAL_ID,
        defaultAddress.municipalCode,
        tenantId,
        COOKIES.SESSION.EXPIRY,
        true,
        whitelabel.domainName,
      );
    }

    const mergeCartErrors = doMergeCart
      ? await mergeCart(tenantId, catalystBaseUrl, whitelabel)
      : [];

    const { cartCount, errors: cartCountErrors } = await getCartCount(
      tenantId,
      catalystBaseUrl,
      additionalInfo,
    );

    if ([firstName, lastName].includes(undefined)) {
      return {
        loginStatus: true,
        data,
        errors: [
          ...associateAccountErrors,
          ...mergeCartErrors,
          ...cartCountErrors,
          CATALYST_LOGIN_ERROR_MISSING_DATA,
        ],
      };
    }

    const emailAsFirstName = emailId.split('@')[0];
    setUserCookies({
      firstName: firstName || emailAsFirstName,
      lastName,
      cartCount,
      userId,
      tenantId,
      domainName: whitelabel.domainName,
      segments,
    });
    return {
      loginStatus: true,
      data: userData,
      errors: [...mergeCartErrors, ...cartCountErrors],
    };
  }

  return {
    errors: [],
    loginStatus: false,
    validationErrors: errors,
  };
}

async function getOTPByEmailURL(emailAddress, tenantId, catalystBaseUrl, otpFlow) {
  return catalystGetOtpByEmail(emailAddress, tenantId, catalystBaseUrl, otpFlow);
}

async function getOTPBySMSURL(emailAddress, tenantId, catalystBaseUrl, otpFlow) {
  return catalystGetOtpBySMS(emailAddress, tenantId, catalystBaseUrl, otpFlow);
}

async function getOTPByChallengeURL(tenantId, catalystBaseUrl, isEmailFlow, otpFlow) {
  return catalystGetOtpByChallenge(tenantId, catalystBaseUrl, isEmailFlow, otpFlow);
}

async function updateUser(userData, tenantId, catalystBaseUrl) {
  return catalystUpdateUser(userData, tenantId, catalystBaseUrl);
}

async function getUser(tenantId, catalystBaseUrl) {
  return catalystGetUser(tenantId, catalystBaseUrl);
}

module.exports = {
  login,
  logout,
  mergeCart,
  associateAccount,
  getCartCount,
  softLogin,
  isLoggedIn,
  userStatus,
  resetPasswordWithCurrent,
  resetPasswordWithoutCurrent,
  legalTextConsent,
  consentTemplate,
  consentTemplateV2,
  saveUserConsent,
  getOTPByEmailURL,
  getOTPBySMSURL,
  getOTPByChallengeURL,
  getOTPChallengeValidationURL,
  loginWithToken,
  updateUser,
  getUser,
};
